import { useQuery } from "@tanstack/react-query";
import { Download, RefreshCw, ScrollText, X } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import { Label } from "@/components/ui/label";
import { EmptyState, PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { Pagination } from "@/components/ui/pagination";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { auditApi, type AuditEntry, type AuditFilters } from "@/lib/api-audit";
import { usePaginationState } from "@/lib/pagination";
import { usersApi } from "@/lib/api-users";

const EVENT_GROUPS: Record<string, string> = {
  login_successful: "auth",
  login_failed: "auth",
  password_changed: "auth",
  api_key_created: "auth",
  api_key_revoked: "auth",
  user_created: "users",
  user_updated: "users",
  user_disabled: "users",
  secret_created: "vault",
  secret_rotated: "vault",
  secret_deleted: "vault",
  canonical_created: "canonical",
  canonical_updated: "canonical",
  canonical_activated: "canonical",
  canonical_deprecated: "canonical",
  canonical_deleted: "canonical",
};

function outcomeTone(outcome: AuditEntry["outcome"]) {
  switch (outcome) {
    case "success":
      return "active" as const;
    case "failure":
      return "danger" as const;
    case "denied":
      return "warning" as const;
  }
}

export function AuditPage() {
  const { t } = useTranslation();
  const [filters, setFilters] = React.useState<AuditFilters>({});
  const { page, pageSize, setPage, setPageSize, reset } = usePaginationState();

  const query = useQuery({
    queryKey: ["audit", filters, page, pageSize],
    queryFn: () => auditApi.list({ ...filters, page, page_size: pageSize }),
    refetchInterval: 30_000,
  });

  // Lookup data: distinct event_type / target_type / actor_id values
  // observed so far, plus a parallel /users fetch so the actor picker
  // can show username + email instead of raw user_ids.
  const facetsQuery = useQuery({
    queryKey: ["audit", "facets"],
    queryFn: () => auditApi.facets(),
    // Facets change slowly — cache for 60s to avoid hammering the DB
    // every time the user opens a dropdown.
    staleTime: 60_000,
  });
  const usersQuery = useQuery({
    queryKey: ["users", "all-for-picker"],
    queryFn: () => usersApi.list({ page_size: 100 }),
    select: (p) => p.items,
    staleTime: 60_000,
  });

  const eventTypeOptions = React.useMemo<ComboboxOption[]>(
    () =>
      (facetsQuery.data?.event_types ?? []).map((v) => ({ value: v, label: v })),
    [facetsQuery.data],
  );
  const targetTypeOptions = React.useMemo<ComboboxOption[]>(
    () =>
      (facetsQuery.data?.target_types ?? []).map((v) => ({ value: v, label: v })),
    [facetsQuery.data],
  );
  // Build a value→label index so existing actor_id filters still render
  // a friendly label even when the underlying user is missing from the
  // first page of users. Falls back to the raw id.
  const actorOptions = React.useMemo<ComboboxOption[]>(() => {
    const users = usersQuery.data ?? [];
    const byId = new Map(users.map((u) => [u.user_id, u]));
    const seenIds = new Set<string>();
    const out: ComboboxOption[] = [];
    // Users first (with username/email).
    users.forEach((u) => {
      seenIds.add(u.user_id);
      out.push({
        value: u.user_id,
        label: u.username,
        description: u.email,
      });
    });
    // Then any audit-observed actor_id not in the users list (system
    // actors, deleted users, API key actors). Use the raw id as label.
    (facetsQuery.data?.actor_ids ?? []).forEach((id) => {
      if (!seenIds.has(id) && !byId.has(id)) {
        out.push({ value: id, label: id });
        seenIds.add(id);
      }
    });
    return out;
  }, [usersQuery.data, facetsQuery.data]);

  function update<K extends keyof AuditFilters>(key: K, value: AuditFilters[K] | "") {
    setFilters((f) => {
      const next = { ...f };
      if (value === "" || value === undefined) {
        delete next[key];
      } else {
        next[key] = value as AuditFilters[K];
      }
      return next;
    });
    reset();
  }

  const items = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const hasFilters = Object.keys(filters).length > 0;

  return (
    <PageContainer>
      <PageHeader
        title={t("audit.title")}
        description={t("audit.subtitle")}
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => query.refetch()}
              disabled={query.isFetching}
            >
              <RefreshCw
                className={"h-3.5 w-3.5 " + (query.isFetching ? "animate-spin" : "")}
                strokeWidth={1.5}
              />
              Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => auditApi.downloadExport(filters)}
            >
              <Download className="h-3.5 w-3.5" strokeWidth={1.5} />
              Export CSV
            </Button>
          </div>
        }
      />
      <PageContent>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          <div className="space-y-1">
            <Label htmlFor="f_event">Event type</Label>
            <Combobox
              id="f_event"
              options={eventTypeOptions}
              value={filters.event_type ?? null}
              onChange={(v) => update("event_type", v ?? "")}
              placeholder="any"
              loading={facetsQuery.isLoading}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="f_actor">Actor</Label>
            <Combobox
              id="f_actor"
              options={actorOptions}
              value={filters.actor_id ?? null}
              onChange={(v) => update("actor_id", v ?? "")}
              placeholder="any"
              loading={usersQuery.isLoading || facetsQuery.isLoading}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="f_target_type">Target type</Label>
            <Combobox
              id="f_target_type"
              options={targetTypeOptions}
              value={filters.target_type ?? null}
              onChange={(v) => update("target_type", v ?? "")}
              placeholder="any"
              loading={facetsQuery.isLoading}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="f_outcome">Outcome</Label>
            <select
              id="f_outcome"
              value={filters.outcome ?? ""}
              onChange={(e) =>
                update("outcome", e.target.value as AuditFilters["outcome"] | "")
              }
              className="w-full"
            >
              <option value="">All outcomes</option>
              <option value="success">success</option>
              <option value="failure">failure</option>
              <option value="denied">denied</option>
            </select>
          </div>
          <div className="space-y-1 flex flex-col">
            <Label>&nbsp;</Label>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setFilters({});
                reset();
              }}
              disabled={!hasFilters}
            >
              <X className="h-3.5 w-3.5" strokeWidth={1.5} />
              Clear filters
            </Button>
          </div>
        </div>

        {items.length > 0 ? (
          <div className="rounded-lg overflow-hidden border border-border/60">
            <Table>
              <Thead>
                <Tr>
                  <Th className="w-32">When</Th>
                  <Th>Event</Th>
                  <Th>Action</Th>
                  <Th>Outcome</Th>
                  <Th>Actor</Th>
                  <Th>Target</Th>
                  <Th>Details</Th>
                </Tr>
              </Thead>
              <Tbody>
                {items.map((e) => (
                  <Tr key={e.audit_id}>
                    <Td className="text-muted-foreground text-xs whitespace-nowrap">
                      {new Date(e.occurred_at).toLocaleString()}
                    </Td>
                    <Td>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs">{e.event_type}</span>
                        {EVENT_GROUPS[e.event_type] && (
                          <Badge>{EVENT_GROUPS[e.event_type]}</Badge>
                        )}
                      </div>
                    </Td>
                    <Td className="text-xs font-mono">{e.action}</Td>
                    <Td>
                      <Badge tone={outcomeTone(e.outcome)}>{e.outcome}</Badge>
                    </Td>
                    <Td className="text-xs">
                      <div className="font-mono">{e.actor_type}</div>
                      <div className="text-muted-foreground truncate max-w-[14rem]">
                        {e.actor_id ?? "—"}
                      </div>
                      {e.actor_ip && (
                        <div className="text-muted-foreground text-xs">{e.actor_ip}</div>
                      )}
                    </Td>
                    <Td className="text-xs">
                      {e.target_type ? (
                        <>
                          <div className="font-mono">{e.target_type}</div>
                          <div className="text-muted-foreground truncate max-w-[14rem]">
                            {e.target_id ?? "—"}
                          </div>
                        </>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </Td>
                    <Td className="text-xs">
                      {e.details ? (
                        <details className="cursor-pointer">
                          <summary className="text-muted-foreground hover:text-foreground select-none">
                            {Object.keys(e.details).length} key(s)
                          </summary>
                          <pre className="mt-1 font-mono text-xs text-muted-foreground whitespace-pre-wrap max-w-md">
                            {JSON.stringify(e.details, null, 2)}
                          </pre>
                        </details>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
            <Pagination
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          </div>
        ) : query.isLoading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : (
          <EmptyState
            icon={ScrollText}
            title="No matching audit entries"
            description={
              hasFilters
                ? "Adjust or clear filters."
                : "Activity will appear here as users and system actions occur."
            }
          />
        )}
      </PageContent>
    </PageContainer>
  );
}
